/**
 * Input → draft: the tap, drag and keyboard models of UX-SPEC §5/§7, all
 * funnelled into the same draft-store operations so the three input methods
 * interoperate (drag a tile, then type the rest, then tap one back…).
 *
 * The rules themselves live in the pure modules (draft.ts, keyboard.ts); this
 * file only reads the current store state and applies the results.
 */
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { cellName } from '@wordloom/engine';
import type { Coord, GameView, RoomView, Tile } from '@wordloom/engine';
import { sfx } from '../audio/sfx';
import { isEditable } from '../lib/inputModality';
import { DEFAULT_CURSOR, useDraftStore } from '../state/draftStore';
import { useRoomStore } from '../state/roomStore';
import { useUiStore } from '../state/uiStore';
import { submitDraft } from './actions';
import { isSquareFree, placementAt, visibleRackIds } from './draft';
import { dragController, type DragHandlers } from './drag';
import { backspace, isArrowKey, moveCursor, toggleDirection, typeLetter } from './keyboard';

function current(): { view: RoomView; game: GameView } | null {
  const view = useRoomStore.getState().view;
  return view?.game ? { view, game: view.game } : null;
}

/** Tiles may be arranged while the game is running (also during the opponent's turn). */
export function canArrange(): boolean {
  const c = current();
  return c !== null && c.game.status === 'playing' && !useDraftStore.getState().pending;
}

function rackTile(game: GameView, tileId: string): Tile | undefined {
  return game.rack.find((t) => t.id === tileId);
}

/** How a tile is spoken: "A", "Blank", "Blank as C". */
function tileName(tile: Tile | undefined, letter?: string): string {
  if (!tile) return 'Tile';
  if (!tile.blank) return tile.letter;
  return letter ? `Blank as ${letter}` : 'Blank';
}

/**
 * Screen-reader feedback for board and rack changes (focus moves on to the
 * next square, so the change itself would otherwise go unspoken).
 */
function announce(text: string): void {
  useUiStore.getState().announce(text);
}

function announcePlaced(game: GameView, tileId: string, row: number, col: number): void {
  const letter = useDraftStore.getState().placements.find((p) => p.tileId === tileId)?.letter;
  announce(`${tileName(rackTile(game, tileId), letter)} placed on ${cellName(row, col)}.`);
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Put a rack (or draft) tile on a square. A blank without a letter opens the
 * letter picker anchored at the square.
 */
export function placeAt(tileId: string, row: number, col: number, opts: { viaDrag?: boolean } = {}): boolean {
  const c = current();
  if (!c || !canArrange()) return false;
  const tile = rackTile(c.game, tileId);
  if (!tile) return false;
  const draft = useDraftStore.getState();
  const previous = draft.placements.find((p) => p.tileId === tileId);
  const ok = draft.place(c.game.board, { tileId, row, col }, opts);
  if (!ok) {
    sfx.invalid();
    return false;
  }
  sfx.place();
  draft.setCursor({ row, col });
  // A blank without a letter is announced by the picker once its letter is chosen.
  if (tile.blank && !previous?.letter) draft.setPicker({ tileId, row, col, mode: 'place' });
  else announcePlaced(c.game, tileId, row, col);
  return true;
}

/** Recall button / Escape: every draft tile goes back to the rack. */
export function recallAllTiles(): void {
  const draft = useDraftStore.getState();
  if (draft.placements.length === 0 || draft.pending) return;
  draft.recallAll();
  sfx.return();
  announce('Tiles returned to rack.');
}

/** Shuffle button / Shift+S. */
export function shuffleRack(): void {
  const c = current();
  if (!c || c.game.status !== 'playing') return;
  useDraftStore.getState().shuffle();
  sfx.shuffle();
  announce('Rack shuffled.');
}

export function tapRackTile(tileId: string): void {
  const draft = useDraftStore.getState();
  if (draft.selectedId === tileId) {
    draft.select(null);
    return;
  }
  if (draft.selectedId && draft.placements.every((p) => p.tileId !== draft.selectedId)) {
    // Two rack taps in a row swap the tiles.
    draft.swapInRack(draft.selectedId, tileId);
    sfx.pick();
    return;
  }
  draft.select(tileId);
  sfx.pick();
}

/** Tap a draft tile on the board: blanks offer "change letter", others go home. */
export function tapBoardDraft(tileId: string): void {
  const c = current();
  const draft = useDraftStore.getState();
  const placement = draft.placements.find((p) => p.tileId === tileId);
  if (!c || !placement || draft.pending) return;
  if (rackTile(c.game, tileId)?.blank) {
    draft.setPicker({ tileId, row: placement.row, col: placement.col, mode: 'change' });
    return;
  }
  draft.recall(tileId);
  sfx.return();
}

/** Tap an empty square: place the selected tile there, or move the cursor. */
export function tapCell(row: number, col: number): void {
  const draft = useDraftStore.getState();
  if (draft.selectedId) {
    placeAt(draft.selectedId, row, col);
    return;
  }
  draft.setCursor({ row, col });
}

// ---------------------------------------------------------------------------
// Drag & drop
// ---------------------------------------------------------------------------

export const dragHandlers: DragHandlers = {
  canDropOnCell(origin, row, col) {
    const c = current();
    if (!c || !canArrange()) return false;
    return isSquareFree(c.game.board, useDraftStore.getState().placements, row, col, origin.tileId);
  },
  onTap(origin) {
    if (origin.kind === 'rack') tapRackTile(origin.tileId);
    else tapBoardDraft(origin.tileId);
  },
  onDragStart(origin) {
    const draft = useDraftStore.getState();
    draft.select(null);
    draft.setDragging(origin.tileId);
    sfx.pick();
  },
  onRackHover(index) {
    useDraftStore.getState().setRackHoverIndex(index);
  },
  onDrop(origin, target) {
    const draft = useDraftStore.getState();
    if (target.kind === 'cell') return placeAt(origin.tileId, target.row, target.col, { viaDrag: true });
    if (target.kind === 'rack') {
      if (origin.kind === 'board') {
        if (draft.pending) return false;
        draft.recall(origin.tileId, target.index);
        sfx.return();
      } else {
        draft.moveInRack(origin.tileId, target.index, { viaDrag: true });
      }
      return true;
    }
    return false;
  },
  onDragEnd() {
    useDraftStore.getState().setDragging(null);
  },
  reducedMotion: prefersReducedMotion,
};

export function installDragHandlers(): () => void {
  dragController.setHandlers(dragHandlers);
  return () => dragController.setHandlers(null);
}

// ---------------------------------------------------------------------------
// Keyboard
// ---------------------------------------------------------------------------

/** Move DOM focus to the cursor square (roving tabindex target). */
export function focusCursorCell(cursor: Coord): void {
  const el = document.querySelector<HTMLElement>(`[data-cell="${cursor.row},${cursor.col}"]`);
  el?.focus({ preventScroll: false });
}

type AnyKeyEvent = KeyboardEvent | ReactKeyboardEvent;

function isLetterKey(e: AnyKeyEvent): boolean {
  return e.key.length === 1 && /[a-z]/i.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
}

/**
 * Board keyboard model. Returns true when the key was handled (the caller
 * then prevents the default action).
 */
export function handleBoardKey(e: AnyKeyEvent): boolean {
  const c = current();
  if (!c) return false;
  const draft = useDraftStore.getState();
  const cursor = draft.cursor ?? DEFAULT_CURSOR;

  if (isArrowKey(e.key) && !e.altKey && !e.metaKey && !e.ctrlKey) {
    const next = moveCursor(cursor, e.key);
    draft.setCursor(next);
    focusCursorCell(next);
    return true;
  }
  if (e.key === ' ' && !e.shiftKey) {
    const direction = toggleDirection(draft.direction);
    draft.setDirection(direction);
    if (!draft.cursor) draft.setCursor(cursor);
    announce(direction === 'H' ? 'Typing across.' : 'Typing down.');
    return true;
  }
  if (isLetterKey(e)) {
    if (!canArrange()) return true;
    const result = typeLetter(
      {
        board: c.game.board,
        placements: draft.placements,
        rack: c.game.rack,
        rackOrder: draft.rackOrder,
        cursor,
        direction: draft.direction,
      },
      e.key,
    );
    if (!result.ok) {
      sfx.invalid();
      if (result.reason === 'no-tile') {
        useUiStore.getState().toast(`No “${e.key.toUpperCase()}” on your rack`, { kind: 'info', id: 'kbd', duration: 1800 });
      }
      return true;
    }
    draft.place(c.game.board, result.placement);
    draft.setCursor(result.cursor);
    focusCursorCell(result.cursor);
    sfx.place();
    announcePlaced(c.game, result.placement.tileId, result.placement.row, result.placement.col);
    return true;
  }
  if (e.key === 'Backspace') {
    if (draft.pending) return true;
    const back = backspace(draft);
    if (back) {
      const tile = rackTile(c.game, back.tileId);
      draft.recall(back.tileId);
      draft.setCursor(back.cursor);
      focusCursorCell(back.cursor);
      sfx.return();
      announce(`${tileName(tile)} returned to rack.`);
    }
    return true;
  }
  if (e.key === 'Delete') {
    const here = placementAt(draft.placements, cursor.row, cursor.col);
    if (here && !draft.pending) {
      draft.recall(here.tileId);
      sfx.return();
      announce(`${tileName(rackTile(c.game, here.tileId))} returned to rack.`);
    }
    return true;
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    const here = placementAt(draft.placements, cursor.row, cursor.col);
    // Enter on a draft blank (re)chooses its letter — typed blanks are
    // auto-lettered, so "no letter yet" is not the only case.
    if (here && rackTile(c.game, here.tileId)?.blank) {
      draft.setPicker({ tileId: here.tileId, row: here.row, col: here.col, mode: here.letter ? 'change' : 'place' });
      return true;
    }
    // A selected rack tile + Enter on a free square places it, like a tap.
    if (!here && draft.selectedId && isSquareFree(c.game.board, draft.placements, cursor.row, cursor.col)) {
      tapCell(cursor.row, cursor.col);
      return true;
    }
    void submitDraft();
    return true;
  }
  return false;
}

/** Rack keyboard: Alt+←/→ moves the focused tile within the rack. */
export function handleRackKey(e: AnyKeyEvent, tileId: string): boolean {
  if (!e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return false;
  const draft = useDraftStore.getState();
  const visible = visibleRackIds(draft.rackOrder, draft.placements);
  const index = visible.indexOf(tileId);
  if (index < 0) return true;
  const target = e.key === 'ArrowLeft' ? index - 1 : index + 1;
  if (target < 0 || target >= visible.length) return true;
  draft.moveInRack(tileId, target);
  sfx.pick();
  const game = current()?.game;
  announce(`Moved ${tileName(game && rackTile(game, tileId))} to position ${target + 1}.`);
  return true;
}

/**
 * Open the exchange dialog / pass confirm from a shortcut. Off-turn the
 * shortcut explains why instead of opening a dialog that can't be used.
 */
export function openTurnDialog(id: 'exchange' | 'pass'): void {
  const c = current();
  const ui = useUiStore.getState();
  if (!c || c.game.status !== 'playing') return;
  if (c.game.currentSeat !== c.view.you) {
    const name = c.view.seats[c.game.currentSeat]?.name ?? 'your opponent';
    ui.toast(`It’s ${name}’s turn — you can ${id} on yours.`, { kind: 'info', id: 'kbd', duration: 2200 });
    sfx.invalid();
    return;
  }
  if (useDraftStore.getState().pending) return;
  ui.openDialog(id);
}

/**
 * Window-level shortcuts for the game screen: Shift+S/E/P, ?, Enter, Escape,
 * and typing letters from anywhere (focus jumps to the board cursor).
 */
export function handleGlobalKey(e: KeyboardEvent): void {
  if (e.defaultPrevented || isEditable(e.target)) return;
  const ui = useUiStore.getState();
  const draft = useDraftStore.getState();
  if (ui.dialog || draft.picker) return;
  if (e.ctrlKey || e.metaKey) return;
  const c = current();
  if (!c) return;

  if (e.key === '?') {
    ui.openDialog('shortcuts');
    e.preventDefault();
    return;
  }
  if (e.altKey) return;
  if (e.shiftKey) {
    const k = e.key.toUpperCase();
    if (k === 'S') shuffleRack();
    else if (k === 'E') openTurnDialog('exchange');
    else if (k === 'P') openTurnDialog('pass');
    else return;
    e.preventDefault();
    return;
  }
  if (e.key === 'Escape') {
    if (draft.placements.length > 0 && !draft.pending) recallAllTiles();
    else if (draft.selectedId) draft.select(null);
    e.preventDefault();
    return;
  }
  const onControl = e.target instanceof HTMLElement && e.target.closest('button, a, [role="button"], summary');
  if (e.key === 'Enter' && !onControl) {
    void submitDraft();
    e.preventDefault();
    return;
  }
  // Typing / arrows / Backspace with focus outside the board: act on the cursor.
  const boardKey = isLetterKey(e) || e.key === 'Backspace' || (isArrowKey(e.key) && !onControl);
  if (boardKey && handleBoardKey(e)) {
    e.preventDefault();
    const cursor = useDraftStore.getState().cursor ?? DEFAULT_CURSOR;
    focusCursorCell(cursor);
  }
}
